import {
  Button,
  ButtonToggle,
  CheckIcon,
  ChevronDownIcon,
  List,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import type { SyntheticEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Form, useSubmit } from 'react-router';

import { BagTransferModal } from '@jane/bloom/components/bagTransferModal/bagTransferModal';
import { AddToCartLabel } from '@jane/bloom/components/productCard/components/addToCartLabel';
import { WEIGHT_LABELS } from '@jane/bloom/dataAccess/constants';
import { useBagTransfer } from '@jane/bloom/hooks/cart/useBagTransfer';
import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { getMaxCartQuantity } from '@jane/bloom/utils/getMaxCartQuantity';
import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type { MmpPriceId } from '@jane/shared/types';

import { AddToCartSkeleton } from './loaders/addToCartSkeleton';

import styles from './productAddToCart.module.css';

export function getDisabledLabel({
  atMaxQuantity = false,
  isAvailableInTheArea = true,
  isOutOfStock = false,
}) {
  if (atMaxQuantity) return 'Limit reached';
  if (isOutOfStock) return 'Out of stock';
  if (!isAvailableInTheArea) return 'Unavailable at this store';

  return 'Add to bag';
}

type ProductAddToCartProps = {
  availableWeights: Array<MmpPriceId>;
  currentWeight: string;
  isAvailableInTheArea: boolean;
  isOutOfStock: boolean;
  isStoreChanging: boolean;
  product: ProductViewModel;
  quantity: number;
  setCurrentWeight: (weight: MmpPriceId) => void;
  setQuantity: (quantity: number) => void;
};

export function ProductAddToCart({
  availableWeights,
  currentWeight,
  isAvailableInTheArea,
  isOutOfStock,
  isStoreChanging,
  product,
  quantity,
  setCurrentWeight,
  setQuantity,
}: ProductAddToCartProps) {
  const submit = useSubmit();
  const {
    fetcher: { state },
    formArgs,
  } = useAddToCartConfig({
    product,
    productId: product.product_id,
    quantity,
    selectedWeight: currentWeight,
  });
  const {
    cancelTransfer,
    checkAndHandleTransfer,
    confirmTransfer,
    newCart,
    showWarningModal,
  } = useBagTransfer();
  const countInCart = useOptimisticBagSize(product.product_id);

  const { cart, shoppingPreferences } = useRootLoaderData();
  const [disabledWeights, setDisabledWeights] = useState<string[]>([]);
  const [maxCartQty, setMaxCartQty] = useState<number>();

  const isAddingToCart = state === 'submitting';

  const onSubmit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    // if cart items in the cart are from a different store, prevent the add to cart action and show warning modal
    const formData = new FormData(event.currentTarget);

    const count = formData.get('count');
    const priceId = formData.get('priceId');
    const productId = formData.get('productId');

    const needsTransfer = checkAndHandleTransfer({
      count: Number(count),
      priceId: priceId ? String(priceId) : undefined,
      productId: Number(productId),
    });

    if (needsTransfer) {
      event.preventDefault();
    }
  };

  const confirmStartNewBag = () => {
    confirmTransfer(() => {
      // Replace the current cart with the new cart
      if (newCart) {
        submit({ shouldReplaceCart: 'true' }, formArgs);
      }
    });
  };

  const getDisabledWeights = useCallback(() => {
    const unavailableWeights: string[] = [];

    availableWeights.forEach((weight) => {
      const maxCartQty = getMaxCartQuantity({
        cart,
        currentProduct: product,
        currentWeight: weight,
        storeId: shoppingPreferences?.storeId,
      });

      if (maxCartQty === 0) {
        unavailableWeights.push(weight);
      }
    });

    return unavailableWeights;
  }, [availableWeights, cart, product, shoppingPreferences]);

  const weightButtons = availableWeights.map((weight) => (
    <ButtonToggle.Button
      label={product.amount ? 'Each' : WEIGHT_LABELS[weight]}
      key={weight}
      value={weight}
      selected={currentWeight === weight}
      onClick={() => {
        setCurrentWeight(weight);
      }}
      disabled={disabledWeights.includes(weight)}
    />
  ));

  useEffect(() => {
    setDisabledWeights(getDisabledWeights());
    setMaxCartQty(
      getMaxCartQuantity({
        cart,
        currentProduct: product,
        currentWeight,
        storeId: shoppingPreferences?.storeId,
      })
    );
  }, [cart, currentWeight, getDisabledWeights, product, shoppingPreferences]);

  const multiWeightDisabled =
    currentWeight !== undefined &&
    availableWeights.length === disabledWeights.length;
  const singleWeightDisabled = currentWeight === undefined && maxCartQty === 0;
  const atMaxQuantity = multiWeightDisabled || singleWeightDisabled;

  const isDisabled = isOutOfStock || !isAvailableInTheArea || atMaxQuantity;
  const disabledLabel = getDisabledLabel({
    atMaxQuantity,
    isAvailableInTheArea,
    isOutOfStock,
  });

  return (
    <Form name="Add to Cart Form" onSubmit={onSubmit} {...formArgs}>
      <input type="hidden" name="productId" value={product.product_id} />
      <input type="hidden" name="priceId" value={currentWeight} />
      <input type="hidden" name="count" value={quantity} />

      {isStoreChanging && <AddToCartSkeleton />}
      {!isStoreChanging && (
        <>
          <BagTransferModal
            open={showWarningModal}
            onConfirm={confirmStartNewBag}
            onRequestClose={cancelTransfer}
            newBag={newCart}
          />
          {!isDisabled && currentWeight && (
            <div className={styles['pdpWeightSelector']}>
              <ButtonToggle
                value={currentWeight}
                variant="tertiary"
                onChange={(newWeight) => {
                  setCurrentWeight(newWeight.toString() as MmpPriceId);
                }}
              >
                {weightButtons}
              </ButtonToggle>
            </div>
          )}
          <div className={styles['pdpQuantitySelectorContainer']}>
            <Popover
              label=""
              openOn={isDisabled ? 'disabled' : 'click'}
              target={
                <div className={styles['pdpQuantitySelector']}>
                  <Button
                    full
                    label={`Qty:\xa0${quantity.toString()}`}
                    variant="tertiary"
                    endIcon={<ChevronDownIcon color="inherit" size="sm" />}
                    disabled={isDisabled}
                  />
                </div>
              }
            >
              {({ closePopover }) =>
                Array.from(
                  { length: maxCartQty && maxCartQty > 0 ? maxCartQty : 0 },
                  (_, ind) => {
                    const quantity = ind + 1;
                    return (
                      <List.Item
                        ariaLabel={`Quantity: ${quantity} `}
                        key={ind}
                        alignment="center"
                        onClick={() => {
                          setQuantity(quantity);
                          closePopover();
                        }}
                      >
                        <Typography>{quantity}</Typography>
                      </List.Item>
                    );
                  }
                )
              }
            </Popover>
            <div className={styles['pdpAddToCartBtnContainer']}>
              <Button
                variant="primary"
                full
                disabled={isDisabled || isAddingToCart}
                startIcon={
                  isAddingToCart && (
                    <CheckIcon className={styles['textContrastColor']} />
                  )
                }
                label={
                  isDisabled ? (
                    disabledLabel
                  ) : (
                    <AddToCartLabel
                      atMaxQuantity={atMaxQuantity}
                      countInCart={countInCart}
                      hasMultipleWeights={false}
                      isBeingAddedToCart={isAddingToCart}
                    />
                  )
                }
                data-testid="pdp-add-to-cart"
                type="submit"
              />
            </div>
          </div>
        </>
      )}
    </Form>
  );
}
